import { CampanhaService, type Campanha } from "@/services/app/campanhas";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Calendar, ChevronRight, Clock, Megaphone, Users } from "lucide-react";
import moment from "moment";
import React from "react";
import { useNavigate } from "react-router-dom";

function Campanhas(): React.JSX.Element {
  const navigate = useNavigate();
  const campanhaService = new CampanhaService();

  const { data: campanhas, isLoading, error } = useQuery<Campanha[]>({
    queryKey: ["campanhas-ativas"],
    queryFn: async () => {
      const response = await campanhaService.getCampanhasAtivas();
      return response;
    },
  });

  moment.locale("pt");

  const formatDate = (dateString: string) => {
    try {
      return moment(dateString).format("DD [de] MMMM [de] YYYY");
    } catch {
      return "Data inválida";
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      return moment(dateString).format("DD/MM/YYYY [às] HH:mm");
    } catch {
      return "Data inválida";
    }
  };

  const isInscricaoAberta = (dataFimInscricao: string) => {
    const now = new Date();
    const fimInscricao = new Date(dataFimInscricao);
    return now <= fimInscricao;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header com botão de voltar */}
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                Campanhas
              </h1>
              <p className="text-sm text-gray-500">
                Gerencie suas campanhas
              </p>
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-4"></div>
              <div className="h-3 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        {/* Header com botão de voltar */}
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                Campanhas
              </h1>
              <p className="text-sm text-gray-500">
                Gerencie suas campanhas
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <div className="text-red-600 mb-2">
            <Megaphone size={48} className="mx-auto mb-4" />
          </div>
          <h3 className="text-lg font-medium text-red-800 mb-2">
            Erro ao carregar campanhas
          </h3>
          <p className="text-red-600">
            Não foi possível carregar as campanhas. Tente novamente mais tarde.
          </p>
        </div>
      </div>
    );
  }

  if (!campanhas || campanhas.length === 0) {
    return (
      <div className="space-y-6">
        {/* Header com botão de voltar */}
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                Campanhas
              </h1>
              <p className="text-sm text-gray-500">
                Gerencie suas campanhas
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
          <div className="text-gray-400 mb-4">
            <Megaphone size={64} className="mx-auto mb-4" />
          </div>
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            Nenhuma campanha disponível
          </h3>
          <p className="text-gray-500">
            No momento não há campanhas abertas para inscrição.
            <br />
            Fique atento às próximas oportunidades!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header com botão de voltar */}
      <div className="flex items-center justify-between h-16">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Campanhas
            </h1>
            <p className="text-sm text-gray-500">
              Gerencie suas campanhas
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {campanhas.map((campanha) => (
          <div
            key={campanha.id}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
          >
            {/* Header da Campanha */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2 py-1 rounded-full">
                    {campanha.tipo.nome}
                  </span>
                  {isInscricaoAberta(campanha.dataFimInscricao) ? (
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                      Inscrições Abertas
                    </span>
                  ) : (
                    <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                      Inscrições Encerradas
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {campanha.nome}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {campanha.descricao}
                </p>
              </div>
            </div>

            {/* Informações da Campanha */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar size={16} className="text-indigo-500" />
                <div>
                  <span className="text-gray-500">Data do evento:</span>
                  <br />
                  <span className="font-medium">
                    {formatDate(campanha.dataEvento)}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock size={16} className="text-orange-500" />
                <div>
                  <span className="text-gray-500">Inscrições até:</span>
                  <br />
                  <span className="font-medium">
                    {formatDateTime(campanha.dataFimInscricao)}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Users size={16} className="text-green-500" />
                <div>
                  <span className="text-gray-500">Vagas:</span>
                  <br />
                  <span className="font-medium">
                    {campanha.vagasDisponiveis} de {campanha.vagasTotal}
                  </span>
                </div>
              </div>
            </div>

            {/* Barra de Progresso das Vagas */}
            <div className="mb-4">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Vagas ocupadas</span>
                <span>
                  {Math.round(
                    ((campanha.vagasTotal - campanha.vagasDisponiveis) /
                      campanha.vagasTotal) *
                      100
                  )}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      ((campanha.vagasTotal - campanha.vagasDisponiveis) /
                        campanha.vagasTotal) *
                      100
                    }%`,
                  }}
                ></div>
              </div>
            </div>

            {/* Botão de Ação */}
            <button
              onClick={() => navigate(`/campanhas/${campanha.id}`)}
              disabled={
                !isInscricaoAberta(campanha.dataFimInscricao) ||
                campanha.vagasDisponiveis === 0
              }
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {!isInscricaoAberta(campanha.dataFimInscricao) ? (
                "Inscrições Encerradas"
              ) : campanha.vagasDisponiveis === 0 ? (
                "Vagas Esgotadas"
              ) : (
                <>
                  Ver Detalhes e Inscrever-se
                  <ChevronRight size={16} />
                </>
              )}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Campanhas;
